/**
 * Profile Incomplete Modal
 * Modal shown when user tries to create a ritual with incomplete profile
 */

import { ChevronRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { CheckmarkIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { User as UserType } from "@/features/auth/store/authStore"
import { getAllProfileItems } from "../utils/profileCompletion"

interface ProfileIncompleteModalProps {
  isOpen: boolean
  user: UserType | null
  closeOnly?: boolean
  onClose?: () => void
}

export function ProfileIncompleteModal({
  isOpen,
  user,
  closeOnly = false,
  onClose,
}: ProfileIncompleteModalProps) {
  const navigate = useNavigate()

  const allItems = user ? getAllProfileItems(user) : []

  const handleCompleteProfile = () => {
    navigate("/profile/edit")
  }

  const handleGoBack = () => {
    if (closeOnly) {
      // Close the modal via callback - parent controls isOpen state
      onClose?.()
    } else {
      navigate(-1)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleGoBack()}>
      <DialogContent showCloseButton className="p-15">
        <DialogHeader className="text-center">
          <DialogTitle className="text-primary text-2xl font-semibold text-center">
            Set Up Your Profile
          </DialogTitle>
          <DialogDescription className="text-primary text-center text-sm leading-relaxed">
            A complete profile helps people trust you and connect with your ritual. <br />
            Rituals created by coaches with full profiles get more visibility and engagement.
          </DialogDescription>
        </DialogHeader>

        {/* Checklist Items */}
        <div className="space-y-3 py-2">
          {allItems.map(item => (
            <div
              key={item.key}
              onClick={handleCompleteProfile}
              className={`flex items-center justify-between p-3 cursor-pointer rounded-lg border ${item.isComplete ? "bg-brand-olive-alpha-40 border-brand-olive" : "bg-card border-input"}`}
            >
              <div className="flex w-full items-center justify-between gap-3 p-1">
                <span
                  className={`text-sm font-medium ${
                    item.isComplete ? "text-primary line-through" : "text-primary font-semibold"
                  }`}
                >
                  {item.label}
                </span>

                {item.isComplete ? (
                  // Completed item - checkmark indicator
                  <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center">
                    <CheckmarkIcon className="h-5 w-5 text-[#000000]" />
                  </div>
                ) : (
                  // Incomplete item - 20% indicator
                  <div className="w-5 h-5 flex items-center justify-center">
                    <ChevronRight onClick={handleCompleteProfile} className="cursor-pointer" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-3 flex items-center !justify-center">
          <div className="flex items-center justify-center">
            <Button
              variant="primary"
              onClick={handleGoBack}
              className="flex-1 rounded-full uppercase px-15"
            >
              Maybe Later
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
